<script lang="ts">
  import { goto } from "$app/navigation";

  import { onMount } from "svelte";

  import GlobalHotKey from "$lib/ui/common/GlobalHotKey.svelte";
  import contents from "./contents.json";

  const asciiLogo = `
              ; ; ; ; ; ; ; ; ; ; ;         ; ; ; ; ; ; ; ; ; ; ;
            * ; ; ; ; ; ; ; ; ; ; ;       ; ; ; ; ; ; ; ; ; ; ; ; .
            ; ; ; ; ;           ; ; *     ; ; ; ; ;           ; ; ;
          : ; ; : ; ;           ; ; ;   + ; ; ; ; ; ;         : ; ; ;
        : ; ; :   ; ; ;           ; ; ; ; ; ;   : ; ; :         ; ; ;
        ; ; ;       ; ; ;         : ; ; ; ;       ; ; ;           ; ; .
      : ; ; +       ; ; ; ; ; ; ; ; ; ; ; ;       ; ; ;           : ; ;
      ; ; ;         . ; ; ; ; ; ; ; ; ; ;           ; ; ;           ; ; ;
    ; ; ;           ; ; ;         ; ; ; ; ;           ; ; ;           ; ; ;
    ; ; ;           ; ;           ; ; ; ; ; *         ; ; ;           ; ; ;
  ; ; ;           ; ; ;           ; ; * ; ; ;           ; ; ;         ; ; ;
  ; ; ;         : ; ;           ; ; +     ; ; ;         + ; ;           : ; ;
; ; ;         * ; ; ;         ; ; ;       ; ; ;           + ; ;           ; ; +
; ; ;         ; ; ;           ; ; :         ; ; ;           ; ; ; ; ; ; ; ; ; ;
; ; +         ; ; +         ; ; ;             ; ;           ; ; ; ; ; ; ; ; ; ; *
; ; ;       ; ; ;           ; ; ;             ; ; ;       ; ; ;           ; ; ;
  ; ; ;   : ; ;           ; ; ;                 ; ; ;   ; ; ;           + ; ;
  ; ; ;   ; ; ;           ; ;                   ; ; ;   ; ; ;           ; ; ;
    ; ; ; ; ; ;         ; ; ;                     ; ; ; ; ; *         ; ; ;
    ; ; ; ; ; ; ; ; ; ; ; ;                       : ; ; ; ; ; ; ; ; ; ; ; ;
      ; ; ; ; ; ; ; ; ; ; ;                         ; ; ; ; ; ; ; ; ; ; ;`;

  const animationDuration = 2000; // Duration in ms

  const easingFunction = (t: number): number =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

  onMount(() => {
    const preElement = document.querySelector("pre");
    if (!preElement) return;

    // Initially hide the pre element
    preElement.style.visibility = "hidden"; // Prevents the element from being interactive or visible
    preElement.style.transition =
      "opacity 0.5s ease-in-out, visibility 0s 0.5s"; // Fade-in effect and delay visibility change

    const startTime = performance.now();

    const renderFrame = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / animationDuration, 1);
      const easedProgress = easingFunction(progress);
      const visibleChars = Math.floor(asciiLogo.length * easedProgress);

      preElement.textContent = asciiLogo.slice(0, visibleChars);

      // Show the pre element and start the fade-in effect
      if (visibleChars > 0) {
        preElement.style.visibility = "visible"; // Make the element visible when animation starts
      }

      if (visibleChars < asciiLogo.length) {
        requestAnimationFrame(renderFrame);
      }
    };

    requestAnimationFrame(renderFrame);
  });

  const nextPage = contents[0].pages[0];

  const goToNextPage = () => {
    if (nextPage) {
      goto(nextPage.href);
    }
  };

  const hotKeys = [
    { hotKey: "ArrowRight", action: goToNextPage },
    { hotKey: "ArrowDown", action: goToNextPage },
  ];
</script>

{#each hotKeys as { hotKey, action }}
  <GlobalHotKey {hotKey} ctrlOrCmd={false} on:hotkey={action} />
{/each}
<div class="block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
  <!-- Width and height need to be set manually so characters don't animate in from the middle. -->
  <pre
    class="font-mono tracking-wide lg:text-md sm:text-sm text-[7px] sm:w-[708px] sm:h-[440px] w-[354px] h-[231px]"></pre>
</div>

{#if nextPage}
  <a
    href={nextPage.href}
    class="flex items-center gap-2 absolute bottom-9 right-14"
    ><span class="hidden sm:block">{nextPage.title}</span><svg
      width="20"
      height="16"
      viewBox="0 0 20 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9.75 2.16666H10.8333V4.33333H13V6.49997H0L0 8.66667H13V10.8334H10.8333V13H9.75V15.1667H13V13H15.1667V10.8334H17.3333V9.74997H18.4167V8.66667H19.5V6.49997H18.4167V5.41667H17.3333V4.33333H15.1667V2.16666H13V0L9.75 0V2.16666Z"
        fill="currentColor"
      />
    </svg>
  </a>
{/if}
